import FieldDestinationProvider from "./common/fields/FieldDestinationProvider.tsx";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Badge, Box, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import { makeModeAwareAccentSurface } from "@entrilia/shared/utilities/colorHelper.ts";
import type { ReportConfiguration } from "../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../context/useClientContext.ts";
import useUserContext from "../../../../../../context/useUserContext.ts";
import GlobalDndProvider from "../../../../../common/GlobalDndProvider.tsx";
import { useGroupsContext } from "../../../report-lists/groups/contexts/GroupsContext.ts";
import { useAppDispatch } from "../../store/hooks/useAppDispatch.ts";
import {
  selectCurrentReport,
  selectCurrentReportType,
  selectIsNewReport,
  selectResetId,
} from "../../store/selectors/currentReportSelectors";
import { selectReportType } from "../../store/selectors/fieldsStateSelectors";
import { selectCurrentDataSetId } from "../../store/selectors/metaDataSelectors";
import { selectTemplates } from "../../store/selectors/reportTemplatesSelectors";
import { useUnsavedReportContext } from "../../contexts/UnsavedReportContext.ts";
import { selectEditDisabled, selectVersionHistoryOpen } from "../../store/selectors/uiSettingsSelectors.ts";
import { applyBiAgentConfiguration } from "../../store/thunks/currentReportThunks.ts";
import { createCurrentReportAction } from "../../store/utils/storeUtils";
import { isChartReportType, usesTabularConfiguration } from "../../utilities/reportTypeHelper.ts";
import { BiAgentPanel } from "../biAgent";
import CollapseContainer from "../common/CollapseContainer.tsx";
import EntityLevelAccess from "../common/EntityLevelAccess.tsx";
import ReportProductName from "../common/ReportProductName.tsx";
import ReportSelector from "../common/report-selector/ReportSelector.tsx";
import PartContainer from "../viewer/common/PartContainer.tsx";
import ReportActionHeader from "./ReportActionHeader.tsx";
import ReportEditor from "./ReportEditor.tsx";
import ChartEditor from "./chart/ChartEditor.tsx";
import ReportTemplates from "./common/ReportTemplates.tsx";
import { ExportStateContextProvider } from "./common/contexts/ExportStateContextProvider.tsx";
import CustomDragLayer from "./common/dragLayer/CustomDragLayer.tsx";
import FieldsComponent from "./common/fields/Fields.tsx";
import ExportAction from "./common/saving/ExportAction.tsx";
import MoreAction from "./common/saving/MoreAction.tsx";
import SaveButton from "./common/saving/SaveButton.tsx";
import ShareReportButton from "./common/sharing/ShareReportButton.tsx";
import { DeveloperPanelWrapper } from "./dev/DeveloperPanelWrapper.tsx";
import FinancialStatementEditor from "./financialStatement/FinancialStatementEditor.tsx";
import ListEditor from "./list/ListEditor.tsx";
import PivotEditor from "./pivot/PivotEditor.tsx";
import TabularEditor from "./tabular/TabularEditor.tsx";
import TileEditor from "./tile/TileEditor.tsx";
import VersionHistoryPanel from "./version-history/VersionHistoryPanel.tsx";
import useVersionHistoryUrlParam from "./version-history/useVersionHistoryUrlParam.ts";

export default function EditReportPage() {
  const dispatch = useAppDispatch();
  const { clientCode, isPortalFeatureEnabled } = useClientContext();
  const { groups } = useGroupsContext();
  const { hasAnyPermission } = useClientPermissionsContext();
  const unsavedReport = useUnsavedReportContext();
  const report = useSelector(selectCurrentReport);
  const reportType = useSelector(selectCurrentReportType);
  const templates = useSelector(selectTemplates);
  const resetId = useSelector(selectResetId);
  const isNewReport = useSelector(selectIsNewReport);
  const fieldsStateReportType = useSelector(selectReportType);
  const currentDataSetId = useSelector(selectCurrentDataSetId);
  const [biAgentOpen, setBiAgentOpen] = useState(false);
  const [exportValuesAsDisplayed, setExportValuesAsDisplayed] = useState(false);
  const versionHistoryOpen = useSelector(selectVersionHistoryOpen);
  const { isEntriliaUser } = useUserContext();
  const editDisabled = useSelector(selectEditDisabled);
  const showAssistant =
    !unsavedReport &&
    reportType !== ReportType.CashFlow &&
    (isEntriliaUser || isPortalFeatureEnabled("SmartViewAssistantEnabled"));

  useVersionHistoryUrlParam();

  const chartReport = useMemo(() => isChartReportType(report?.reportType), [report]);
  const showExportAction = reportType !== ReportType.Tile;

  const handleApplyConfiguration = useCallback(
    (configuration: ReportConfiguration, updatedReportType?: ReportType, dataSetId?: string) => {
      dispatch(applyBiAgentConfiguration({ configuration, reportType: updatedReportType, dataSetId }));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(createCurrentReportAction("updateReportMode", true));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  if (report === undefined) return <></>;

  if (!hasAnyPermission(["ManageReports"])) {
    return <Navigate to={".."} />;
  }

  return (
    <ExportStateContextProvider
      disabled={editDisabled}
      valueSettings={{ value: exportValuesAsDisplayed, onChange: setExportValuesAsDisplayed }}
    >
      <GlobalDndProvider>
        <FieldDestinationProvider key={`${report.reportId}-${resetId}`}>
          <Stack sx={{ flex: 1 }}>
            <ReportActionHeader
              readonlyMode={editDisabled}
              ProductName={<ReportProductName title="Reporting" />}
              ReportSelector={
                <ReportSelector requiredAccess={ReportAccessType.Write} groups={groups} readonlyMode={editDisabled} />
              }
            >
              <Stack direction={"row"} sx={{ alignItems: "center", gap: 1 }}>
                <EntityLevelAccess />
                {!unsavedReport && templates.length > 0 && (
                  <ReportTemplates templates={templates} clientCode={clientCode} disabled={editDisabled} />
                )}
                {!unsavedReport && <ShareReportButton disabled={editDisabled} />}
                {showExportAction && <ExportAction />}
                {!unsavedReport && <SaveButton report={report} disabled={editDisabled} />}
                {!unsavedReport && !isNewReport && <MoreAction report={report} readonlyMode={editDisabled} />}
                {showAssistant && (
                  <Badge
                    badgeContent={
                      <Box component="span" sx={{ pointerEvents: "auto", cursor: "default" }}>
                        New
                      </Box>
                    }
                    sx={(t) => ({
                      "& .MuiBadge-badge": {
                        ...makeModeAwareAccentSurface(t, blue),
                        fontWeight: 500,
                        pointerEvents: "none",
                      },
                    })}
                  >
                    <Tooltip title="SmartViews Assistant">
                      <IconButton
                        onClick={() => setBiAgentOpen(!biAgentOpen)}
                        color={biAgentOpen ? "primary" : "secondary"}
                        disabled={editDisabled}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </Tooltip>
                  </Badge>
                )}
              </Stack>
            </ReportActionHeader>
            <Divider />
            <Box sx={{ display: "flex", flex: 1 }}>
              <CollapseContainer
                expandCollapsePaddingTop={25}
                collapseElement={
                  <Typography
                    variant="h6"
                    sx={(theme) => ({ color: theme.palette.text.primary, transform: "rotate(90deg)" })}
                  >
                    Fields
                  </Typography>
                }
              >
                <PartContainer sx={{ maxWidth: "21rem", minWidth: "21rem" }} caption={<Box></Box>}>
                  <FieldsComponent />
                </PartContainer>
              </CollapseContainer>
              <React.Fragment key={resetId}>
                {(!reportType || reportType === ReportType.Pivot) && (
                  <ReportEditor report={report} Editor={PivotEditor} />
                )}
                {usesTabularConfiguration(reportType) && (
                  <ReportEditor key={reportType} report={report} Editor={TabularEditor} />
                )}
                {chartReport && <ReportEditor report={report} Editor={ChartEditor} />}
                {reportType === ReportType.Tile && <ReportEditor report={report} Editor={TileEditor} />}
                {reportType === ReportType.List && <ReportEditor report={report} Editor={ListEditor} />}
                {reportType === ReportType.FinancialStatement && (
                  <ReportEditor report={report} Editor={FinancialStatementEditor} />
                )}
              </React.Fragment>
              <DeveloperPanelWrapper />

              {showAssistant && (
                <BiAgentPanel
                  open={biAgentOpen}
                  onClose={() => setBiAgentOpen(false)}
                  currentReportType={fieldsStateReportType ?? undefined}
                  currentDataSetId={currentDataSetId ?? undefined}
                  onApplyConfiguration={handleApplyConfiguration}
                />
              )}
              {versionHistoryOpen && !unsavedReport && <VersionHistoryPanel />}
            </Box>
          </Stack>
          <CustomDragLayer />
        </FieldDestinationProvider>
      </GlobalDndProvider>
    </ExportStateContextProvider>
  );
}
